import { TaskResponse } from "@9amhealth/openapi";
import { Blac, Cubit } from "blac-next";
import { getsGlp1Through9am } from "src/lib/refillCohort";
import {
  iconUrl9amInjector,
  iconUrlClipboardCheck
} from "src/constants/iconUrls";
import { StorageController } from "src/state/StorageBloc/StorageBloc";
import TaskManagementBloc, {
  TaskKey,
  TaskObserverEvent,
  TaskResponseKnown,
  transferGlp1PrescriptionTasks
} from "src/state/TaskManagementBloc/TaskManagementBloc";
import { ProfileAttributesKeys } from "src/state/UserCubit/UserCubit";
import { userState } from "src/state/state";

export enum Variant {
  waitingForHIE = "waitingForHIE",
  labsIncomplete = "labsIncomplete",
  labOrderInProgressAndTasksOpen = "labOrderInProgressAndTasksOpen",
  labOrderInProgressAndTasksFinished = "labOrderInProgressAndTasksFinished",
  labUploadBeingReviewed = "labUploadBeingReviewed",
  labsDoneAndPrescriptionTaskDoneAndTransferInReview = "labsDoneAndPrescriptionTaskDoneAndInReview",
  labsDoneAndTasksOpen = "labsDoneAndTasksOpen"
}

export interface NotificationCardState {
  variant: Variant | null;
  variantImage: string | null;
  variantImageIsWide: boolean;
  dismissedVariant: Variant | null;
  isVisible: boolean;
}

const STORAGE_KEY = "notificationCard.dismissedVariant";

const initialState: NotificationCardState = {
  variant: null,
  variantImage: null,
  variantImageIsWide: false,
  dismissedVariant: null,
  isVisible: false
};

export default class NotificationCardCubit extends Cubit<NotificationCardState> {
  private taskManagementBloc = Blac.getBloc(TaskManagementBloc);
  private taskObserver: ReturnType<
    typeof this.taskManagementBloc.addObserver
  > | null = null;

  constructor() {
    super(initialState);
    this.init();
  }

  private init = () => {
    const stored = StorageController.getItem(STORAGE_KEY);
    if (stored && Object.values(Variant).includes(stored as Variant)) {
      this.patch({ dismissedVariant: stored as Variant });
    }

    this.taskObserver = this.taskManagementBloc.addObserver(
      TaskObserverEvent.TASK_LIST_CHANGED,
      this.handleTaskUpdate
    );

    this.updateVariant();
  };

  private handleTaskUpdate = () => {
    this.updateVariant();
  };

  private isGlp1ContinuationWithConciergeCare(): boolean {
    return getsGlp1Through9am();
  }

  private isReviewInProgress(): boolean {
    return Boolean(
      userState.state.profileAttributes?.[
        ProfileAttributesKeys.medicationTransferIsInReview
      ]
    );
  }

  private isTaskDone(task: TaskResponseKnown | undefined): boolean {
    return (
      task?.status === TaskResponse.status.COMPLETED ||
      task?.status === TaskResponse.status.SKIPPED
    );
  }

  private areTransferYourPrescriptionTasksDone(): boolean {
    const presentTasks = transferGlp1PrescriptionTasks
      .map((key) => this.taskManagementBloc.getTask(key))
      .filter((task): task is TaskResponseKnown => Boolean(task));

    if (presentTasks.length === 0) {
      return false;
    }

    return presentTasks.every((task) => this.isTaskDone(task));
  }

  private needsMedicationTransferReviewStatus(): boolean {
    if (!this.isGlp1ContinuationWithConciergeCare()) {
      return false;
    }

    const labOrderTask = this.taskManagementBloc.getTask(
      TaskKey.COMPLETE_INITIAL_LAB_ORDER
    );
    const labOrderDone =
      labOrderTask?.status === TaskResponse.status.COMPLETED ||
      labOrderTask?.status === TaskResponse.status.SKIPPED;

    return labOrderDone && this.areTransferYourPrescriptionTasksDone();
  }

  private async fetchMedicationTransferReviewIfNeeded(): Promise<void> {
    if (!this.needsMedicationTransferReviewStatus()) {
      return;
    }

    // Skip if already fetched
    if (
      userState.state.profileAttributes?.[
        ProfileAttributesKeys.medicationTransferIsInReview
      ] !== undefined
    ) {
      return;
    }

    await userState.fetchAndMergeProfileAttributes([
      ProfileAttributesKeys.medicationTransferIsInReview
    ]);
  }

  private getVariant(): Variant | null {
    const uploadLabsTask = this.taskManagementBloc.getTask(TaskKey.UPLOAD_LABS);
    const labOrderTask = this.taskManagementBloc.getTask(
      TaskKey.COMPLETE_INITIAL_LAB_ORDER
    );

    // Variants 1-4, 6-7: only for HWJ_ON_GLP1_CONTINUATION members
    if (this.isGlp1ContinuationWithConciergeCare()) {
      // 1. waitingForHIE: upload_lab_report is LOCKED
      if (uploadLabsTask?.status === TaskResponse.status.LOCKED) {
        return Variant.waitingForHIE;
      }

      // 2. labsIncomplete: upload_lab_report is AVAILABLE
      if (uploadLabsTask?.status === TaskResponse.status.AVAILABLE) {
        return Variant.labsIncomplete;
      }

      // 3-4. complete-initial-lab-order is IN_PROGRESS
      if (labOrderTask?.status === TaskResponse.status.IN_PROGRESS) {
        return this.areTransferYourPrescriptionTasksDone()
          ? Variant.labOrderInProgressAndTasksFinished
          : Variant.labOrderInProgressAndTasksOpen;
      }

      // 6. complete-initial-lab-order is COMPLETED or SKIPPED (CADENCE_SATISFIED)
      //    + Transfer your prescription tasks COMPLETED or SKIPPED
      //    + transfer review is in progress
      const labOrderDone =
        labOrderTask?.status === TaskResponse.status.COMPLETED ||
        labOrderTask?.status === TaskResponse.status.SKIPPED;

      if (
        labOrderDone &&
        this.areTransferYourPrescriptionTasksDone() &&
        this.isReviewInProgress()
      ) {
        return Variant.labsDoneAndPrescriptionTaskDoneAndTransferInReview;
      }

      // 7. complete-initial-lab-order is COMPLETED or SKIPPED (CADENCE_SATISFIED)
      //    + Transfer your prescription tasks are not all COMPLETED or SKIPPED
      if (labOrderDone && !this.areTransferYourPrescriptionTasksDone()) {
        return Variant.labsDoneAndTasksOpen;
      }
    }

    // 5. labUploadBeingReviewed: upload_lab_report is IN_PROGRESS (for everyone)
    if (uploadLabsTask?.status === TaskResponse.status.IN_PROGRESS) {
      return Variant.labUploadBeingReviewed;
    }

    return null;
  }

  private getVariantImage(variant: Variant | null) {
    switch (variant) {
      case Variant.waitingForHIE:
        return { variantImage: null, variantImageIsWide: false };
      case Variant.labsIncomplete:
      case Variant.labUploadBeingReviewed:
        return {
          variantImage: iconUrlClipboardCheck,
          variantImageIsWide: false
        };
      case Variant.labOrderInProgressAndTasksOpen:
      case Variant.labOrderInProgressAndTasksFinished:
      case Variant.labsDoneAndPrescriptionTaskDoneAndTransferInReview:
      case Variant.labsDoneAndTasksOpen:
        return {
          variantImage: iconUrl9amInjector,
          variantImageIsWide: true
        };
    }

    return { variantImage: null, variantImageIsWide: false };
  }

  updateVariant = () => {
    if (this.needsMedicationTransferReviewStatus()) {
      void this.fetchAndApplyVariant();
      return;
    }
    this.applyVariant();
  };

  private async fetchAndApplyVariant() {
    await this.fetchMedicationTransferReviewIfNeeded();
    this.applyVariant();
  }

  private applyVariant() {
    const newVariant = this.getVariant();

    const { variantImage, variantImageIsWide } =
      this.getVariantImage(newVariant);

    const { variant: currentVariant, dismissedVariant } = this.state;

    // If variant changed and it's different from the dismissed one, reset dismiss state
    if (newVariant && dismissedVariant && newVariant !== dismissedVariant) {
      StorageController.removeItem(STORAGE_KEY);
      this.patch({
        variant: newVariant,
        variantImage,
        variantImageIsWide,
        dismissedVariant: null,
        isVisible: true
      });
      return;
    }

    const isVisible = Boolean(newVariant && newVariant !== dismissedVariant);

    if (newVariant !== currentVariant || isVisible !== this.state.isVisible) {
      this.patch({
        variant: newVariant,
        variantImage,
        variantImageIsWide,
        isVisible
      });
    }
  }

  dismiss = () => {
    const { variant } = this.state;
    if (variant) {
      StorageController.setItem(STORAGE_KEY, variant);
      this.patch({
        dismissedVariant: variant,
        isVisible: false
      });
    }
  };

  _dispose(): void {
    // @ts-expect-error - taskObserver is not typed
    this.taskManagementBloc.removeObserver(this.taskObserver);
    this.taskObserver = null;
    super._dispose();
  }
}
